import { useState, useEffect } from 'react';
import axios from 'axios';
import { Send, Mail } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DistributionManagement = () => {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [certificationTypes, setCertificationTypes] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedCertType, setSelectedCertType] = useState('');
  const [expiryDays, setExpiryDays] = useState(365);
  const [dueDays, setDueDays] = useState(30);
  const [sendEmail, setSendEmail] = useState(false);
  const [distributing, setDistributing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchStudents();
    fetchClasses();
    fetchCertTypes();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${API}/students`);
      setStudents(response.data);
    } catch (error) {
      toast.error('Failed to fetch students');
    }
  };

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/classes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(response.data.filter(c => !c.archived));
    } catch (error) {
      console.error('Failed to fetch classes');
    }
  };

  const fetchCertTypes = async () => {
    try {
      const response = await axios.get(`${API}/vouchers/types`);
      setCertificationTypes(response.data.types);
      if (response.data.types.length > 0) {
        setSelectedCertType(response.data.types[0]);
      }
    } catch (error) {
      toast.error('Failed to fetch certification types');
    }
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(s => s.id));
    }
  };

  const handleSelectStudent = (studentId) => {
    if (selectedStudents.includes(studentId)) {
      setSelectedStudents(selectedStudents.filter(id => id !== studentId));
    } else {
      setSelectedStudents([...selectedStudents, studentId]);
    }
  };

  const handleDistribute = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    if (!selectedCertType) {
      toast.error('Please select a certification type');
      return;
    }

    setDistributing(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/distributions`, {
        student_ids: selectedStudents,
        certification_type: selectedCertType,
        class_id: selectedClass === 'all' ? null : selectedClass,
        expiry_days: parseInt(expiryDays),
        due_days: parseInt(dueDays),
        send_email: sendEmail,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Successfully distributed ${response.data.length} vouchers`);
      setSelectedStudents([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to distribute vouchers');
    } finally {
      setDistributing(false);
    }
  };

  const filteredStudents = students.filter(student => {
    const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClass = selectedClass === 'all' || (student.class_ids && student.class_ids.includes(selectedClass));
    return matchesSearch && matchesClass;
  });

  const getClassNames = (classIds) => {
    if (!classIds || classIds.length === 0) return 'No Classes';
    const classNames = classIds
      .map(id => {
        const cls = classes.find(c => c.id === id);
        return cls ? `${cls.name} (${cls.semester} ${cls.year})` : null;
      })
      .filter(Boolean);
    return classNames.length > 0 ? classNames.join(', ') : 'No Classes';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="distribution-management">
      <div>
        <h2 className="text-4xl font-bold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Distribute Vouchers
        </h2>
        <p className="text-slate-600 dark:text-slate-400">Select students and distribute specific voucher types</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-slate-200 dark:border-slate-700" >
          <CardHeader>
            <CardTitle>Select Students</CardTitle>
            <CardDescription>
              Choose students who will receive vouchers
            </CardDescription>
            <div className="flex gap-4 mt-4">
              <Input
                placeholder="Search students..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="search-students-input"
                className="flex-1"
              />
              <Select value={selectedClass} onValueChange={setSelectedClass}>
                <SelectTrigger className="w-64" data-testid="class-filter-select">
                  <SelectValue placeholder="Filter by class" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Classes</SelectItem>
                  {classes.map(cls => (
                    <SelectItem key={cls.id} value={cls.id}>
                      {cls.name} ({cls.semester} {cls.year})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center space-x-2 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700" >
                <Checkbox
                  id="select-all"
                  checked={selectedStudents.length === filteredStudents.length && filteredStudents.length > 0}
                  onCheckedChange={handleSelectAll}
                  data-testid="select-all-checkbox"
                />
                <label htmlFor="select-all" className="font-medium cursor-pointer flex-1">
                  Select All ({filteredStudents.length} students)
                </label>
              </div>
              
              <div className="max-h-96 overflow-y-auto space-y-2">
                {filteredStudents.length === 0 ? (
                  <div className="text-center text-slate-500 py-8">
                    No students found. Add students first.
                  </div>
                ) : (
                  filteredStudents.map((student) => (
                    <div
                      key={student.id}
                      className="flex items-center space-x-2 p-3 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 transition-colors"
                      data-testid={`student-checkbox-${student.email}`}
                    >
                      <Checkbox
                        id={student.id}
                        checked={selectedStudents.includes(student.id)}
                        onCheckedChange={() => handleSelectStudent(student.id)}
                      />
                      <label htmlFor={student.id} className="cursor-pointer flex-1">
                        <div className="font-medium">{student.name}</div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">{student.email}</div>
                        {student.class_ids && student.class_ids.length > 0 && (
                          <div className="text-xs text-slate-500 dark:text-slate-500">{getClassNames(student.class_ids)}</div>
                        )}
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 dark:border-slate-700" >
          <CardHeader>
            <CardTitle>Distribution Settings</CardTitle>
            <CardDescription>
              Configure voucher distribution options
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor="cert-type">Certification Type</Label>
              <Select value={selectedCertType} onValueChange={setSelectedCertType}>
                <SelectTrigger data-testid="cert-type-select" className="mt-2">
                  <SelectValue placeholder="Select certification type" />
                </SelectTrigger>
                <SelectContent>
                  {certificationTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Only {selectedCertType} vouchers will be distributed
              </p>
            </div>

            <div>
              <Label htmlFor="due-days">Due Date (Days)</Label>
              <Input
                id="due-days"
                type="number"
                value={dueDays}
                onChange={(e) => setDueDays(e.target.value)}
                data-testid="due-days-input"
                min="1"
                className="mt-2"
              />
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Students should complete exam within {dueDays} days
              </p>
            </div>

            <div>
              <Label htmlFor="expiry">Expiry Period (Days)</Label>
              <Input
                id="expiry"
                type="number"
                value={expiryDays}
                onChange={(e) => setExpiryDays(e.target.value)}
                data-testid="expiry-days-input"
                min="1"
                className="mt-2"
              />
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Vouchers will expire after {expiryDays} days
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="send-email"
                checked={sendEmail}
                onCheckedChange={setSendEmail}
                data-testid="send-email-checkbox"
              />
              <label htmlFor="send-email" className="text-sm cursor-pointer">
                Send email notification to students
              </label>
            </div>

            <div className="pt-4 border-t border-slate-200 dark:border-slate-700" >
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-4">
                <div className="text-sm text-slate-700 dark:text-slate-300 space-y-1">
                  <div><strong>Selected:</strong> {selectedStudents.length} student(s)</div>
                  <div><strong>Type:</strong> {selectedCertType || 'Not selected'}</div>
                  <div><strong>Class Filter:</strong> {selectedClass === 'all' ? 'All classes' : getClassNames([selectedClass])}</div>
                  <div><strong>Auto Follow-up:</strong> Every 3 days until reported</div>
                </div>
              </div>
              
              <Button
                onClick={handleDistribute}
                disabled={distributing || selectedStudents.length === 0}
                data-testid="distribute-button"
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-400 text-white hover:from-emerald-600 hover:to-teal-500"
              >
                {distributing ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Distributing...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    <span>Distribute Vouchers</span>
                  </div>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DistributionManagement;
